import {observer} from 'mobx-react';
import {t, withState} from 'shared/core';
import useModal from 'shared/hooks/useModal';
import {endpoints, types} from 'main/tools';
import {TimeTrackingDimensionValue} from 'main/stores/time_tracking';
import {ActionLink, AvatarList, Button} from 'shared/components';
import {InteractiveTable, DropdownFilter} from 'main/components';
import {FormattedMessage} from 'react-intl';
import EditDimensionValueModal from './EditDimensionValueModal';
import ArchiveDimensionValueModal from './ArchiveDimensionValueModal';
import UnarchiveDimensionValueModal from './UnarchiveDimensionValueModal';
import {DimensionPanelState} from '../state';

const COLUMNS = [
  {
    header: 'time_tracking.dimensions.directory.Name',
    width: 6,
    attribute: 'name'
  },
  {
    header: 'time_tracking.dimensions.directory.Employees',
    width: 5,
    render: model => <AvatarList employees={model.employees} moreModalEnabled/>
  }
];

const LOCALIZATION = {
  emptyState: 'time_tracking.dimensions.directory.No values to display',
  removeModal: {
    header: 'time_tracking.dimensions.directory.Remove Value?',
    subHeader: 'time_tracking.dimensions.directory.Are you sure you want to remove this value?',
    body: 'time_tracking.dimensions.directory.REMOVE_VALUE_DESCRIPTION'
  }
};

function statusFilters() {
  return [
    {
      id: 'active',
      display: t('time_tracking.dimensions.directory.Active')
    },
    {
      id: 'archived',
      display: t('time_tracking.dimensions.directory.Archived')
    }
  ];
}

const HeaderActions = observer(({dimension, onEditDimension}) => {
  if (!dimension.canPatch) return null;

  return (
    <ActionLink onClick={onEditDimension}>
      <i className='material-symbols-solid align-middle submarine ml1 mb1'>{'edit'}</i>
    </ActionLink>
  );
});

const AddValueButton = observer(({uiState}) => {
  return (
    <Button size='sm' onClick={() => uiState.openEditDimensionValueModal()}>
      <FormattedMessage id='time_tracking.dimensions.directory.+ Add Value'/>
    </Button>
  );
});

const DimensionPanel = observer(({uiState, onEditDimension}) => {
  const editDimensionValueModalOpen = useModal(uiState, 'EditDimensionValueModal', 'editingDimensionValue', TimeTrackingDimensionValue, 'errors');
  const archiveDimensionValueModalOpen = useModal(uiState, 'ArchiveDimensionValueModal', 'editingDimensionValue', TimeTrackingDimensionValue, 'errors');
  const unarchiveDimensionValueModalOpen = useModal(uiState, 'UnarchiveDimensionValueModal', 'editingDimensionValue', TimeTrackingDimensionValue, 'errors');
  const {dimension} = uiState;

  return (
    <>
      <InteractiveTable
        title={dimension.name}
        uiState={uiState}
        searchable
        columns={COLUMNS}
        onMount={agent => uiState.setAgent(agent)}
        localization={LOCALIZATION}
        tableActionButtons={<AddValueButton uiState={uiState}/>}
        titleActions={<HeaderActions dimension={dimension} onEditDimension={onEditDimension}/>}
        onRemove={dimensionValue => uiState.removeDimensionValue(dimensionValue)}
        onEdit={dimensionValue => uiState.openEditDimensionValueModal(dimensionValue)}
        customLinks={dimensionValue => uiState.customLinksForDimensionValue(dimensionValue)}
        proxy={{
          type: 'async',
          modelType: types.TIME_TRACKING.DIMENSION_VALUE,
          model: TimeTrackingDimensionValue,
          endpoint: endpoints.TIME_TRACKING.DIMENSION_VALUES.FOR_DIMENSION(dimension.id),
          pageSize: 10
        }}>
        <DropdownFilter options={statusFilters()} attr='status'/>
      </InteractiveTable>
      <EditDimensionValueModal uiState={uiState} modalOpen={editDimensionValueModalOpen}/>
      <ArchiveDimensionValueModal uiState={uiState} modalOpen={archiveDimensionValueModalOpen}/>
      <UnarchiveDimensionValueModal uiState={uiState} modalOpen={unarchiveDimensionValueModalOpen}/>
    </>
  );
});

export default withState(DimensionPanel, DimensionPanelState);
